import {
  ChangeDetectionStrategy,
  Component,
  afterNextRender,
  signal,
  DestroyRef,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-reading-progress-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="progress-bar"
      role="progressbar"
      [attr.aria-valuenow]="Math.round(progress() * 100)"
      aria-valuemin="0"
      aria-valuemax="100"
      [style.transform]="'scaleX(' + progress() + ')'"
    ></div>
  `,
  styles: `
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 50;
      height: 3px;
      background: transparent;
    }

    .progress-bar {
      height: 100%;
      background: var(--color-action-primary);
      transform-origin: left;
      transform: scaleX(0);
      will-change: transform;
    }

    @media (prefers-reduced-motion: reduce) {
      :host {
        display: none;
      }
    }
  `,
})
export class ReadingProgressBarComponent {
  protected readonly Math = Math;
  protected readonly progress = signal(0);

  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      const onScroll = () => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight;
        const viewportHeight = window.innerHeight;
        const maxScroll = docHeight - viewportHeight;
        this.progress.set(maxScroll > 0 ? Math.min(scrollTop / maxScroll, 1) : 0);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      this.destroyRef.onDestroy(() => window.removeEventListener('scroll', onScroll));
    });
  }
}
